/**
 * Shared PGF Standard player cell - PGF Draft Board Sweep, Item 6 +
 * Follow-up 3 (4K/large-screen typography scaling). Extracted from
 * auction's SoldCell + snake's PickCell (near-identical twins). Both
 * StandaloneDraftBoard.jsx (auction) and SnakeStandaloneDraftBoard.jsx
 * (snake) consume this component, differing only via `showPaid` (auction
 * true, snake false) - the auction-only $ amount is the sole format
 * difference per the governing convergence rule.
 *
 * Layout (locked against the approved mock): position top-left 22px,
 * team-bye top-right (untouched - correct at every viewport size).
 * First name sits directly above the last name (dominant) with a small
 * gap. $paid renders bottom-right when showPaid.
 *
 * [PGF Board Tile Fixes item 1 - founder root-cause call] Name sizing is
 * a HEIGHT-INDEPENDENT CSS CLAMP, identical to Traditional's known-good
 * `clamp(20px, 3.2vw, 32px)`, so a PGF last name reads exactly the same
 * size as the Traditional sticker at the same team count. The JS
 * measure-and-shrink hook (`useAutoFitText`) was REMOVED from this cell -
 * it kept producing wrong sizes (0, then blown-out, then too small)
 * because it measured a grid cell whose height is itself a function of
 * the content it was measuring. A clamp has nothing to mismeasure.
 */
import React from "react";

/**
 * Shared min-height for one pick cell. Exported so the shared `TeamTile`
 * and both boards' grids size off ONE constant instead of three literals
 * (Board Tile Polish item 1: a team tile is the same height as a player
 * card).
 */
export const PICK_CELL_MIN_H = 96;

// Matches TraditionalDraftBoard's sticker name exactly (item 1 target).
const LAST_NAME_MAX_PX = 32;
// Proportionally smaller (~45%) so the stacked block stays consistent.
const FIRST_NAME_FONT_SIZE = "clamp(9px, 1.45vw, 15px)";

/**
 * [Board Tile Polish item 2] Long hyphenated last names (KEENEY-JAMES,
 * CROSKEY-MERRITT) overflowed the fixed 32px ceiling and clipped, because
 * `word-break: keep-all` correctly refuses to split them. The ceiling now
 * steps down BY CHARACTER COUNT.
 *
 * This is a pure deterministic function of the string - no refs, no
 * measurement, no shrink loop. That is the point: the measure-and-fit
 * approach is what produced the invisible / exploded / tiny-name cascade,
 * and it must not come back. Short names are untouched at full size.
 *
 * Buckets are derived from the worst case in the founder's screenshot -
 * 12 teams at 1920px, which leaves ~132px of usable cell width - and from
 * Barlow Semi Condensed Black's uppercase advance of ~0.46em per letter
 * (~0.28em for a hyphen). Required px = 132 / (0.46 * letters + 0.28 *
 * hyphens). To retune for a different density, redo that arithmetic; do
 * NOT reach for a measurement hook.
 */
export function lastNameMaxPx(lastName) {
  const len = (lastName || "").length;
  if (len <= 9) return LAST_NAME_MAX_PX;  // KELLY, HILL, MCCAFFREY - unchanged
  if (len <= 11) return 27;
  if (len <= 13) return 24;               // KEENEY-JAMES (12)
  if (len <= 16) return 18;               // CROSKEY-MERRITT (15), WESTBROOK-IKHINE (16)
  return 16;
}

const POSITION_COLORS = {
  QB: "#E03030", RB: "#00C853", WR: "#007AFF",
  TE: "#C97000", K: "#B0B0B0", DEF: "#B0B0B0",
};
const SUFFIXES = new Set(["Jr.", "Jr", "Sr.", "Sr", "II", "III", "IV", "V"]);

function splitName(fullName) {
  const nameParts = (fullName || "?").split(" ");
  const lastWord = nameParts[nameParts.length - 1] || "";
  const hasSuffix = SUFFIXES.has(lastWord) && nameParts.length >= 3;
  const firstName = hasSuffix
    ? nameParts.slice(0, -2).join(" ")
    : nameParts.slice(0, -1).join(" ");
  const lastName = hasSuffix
    ? `${nameParts[nameParts.length - 2]} ${lastWord}`
    : (lastWord || fullName || "?");
  return { firstName, lastName };
}

export default function PlayerCell({ player, paid = null, isDark, showPaid = false, onClick, testId }) {
  const pos = player?.position;
  const color = POSITION_COLORS[pos] || "#888";
  const { firstName, lastName } = splitName(player?.name);
  const teamBye = [player?.nfl_team, player?.bye_week ? `${player.bye_week}` : null]
    .filter(Boolean).join("-");

  const nameColor = isDark ? "#F5F5F5" : "#0A0A0A";
  const dim = isDark ? "rgba(255,255,255,.62)" : "rgba(0,0,0,.55)";
  const paidColor = isDark ? "#00C853" : "#005A20";

  return (
    <div
      className="rounded-lg p-1.5 h-full flex flex-col cursor-pointer transition-all hover:brightness-110 relative"
      style={{ background: `${color}1F`, border: `1px solid ${color}30` }}
      data-testid={testId}
      onClick={() => onClick?.(player)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onClick?.(player); }}
    >
      <div className="flex items-start justify-between gap-1">
        <span
          className="font-display font-black"
          style={{ fontSize: 22, lineHeight: 0.8, color, letterSpacing: "-0.01em" }}
        >
          {pos}
        </span>
        <span
          className="font-body font-bold text-right"
          style={{ fontSize: 11, color: dim, marginTop: 2 }}
        >
          {teamBye}
        </span>
      </div>
      <div className="flex-1 flex flex-col justify-end items-center gap-0.5 min-h-0">
        <span
          className="font-body font-bold text-center leading-none max-w-full"
          style={{
            color: dim,
            fontSize: FIRST_NAME_FONT_SIZE,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {firstName}
        </span>
        <span
          className="font-display font-black uppercase text-center w-full"
          style={{
            color: nameColor,
            fontSize: `clamp(14px, 3.2vw, ${lastNameMaxPx(lastName)}px)`,
            lineHeight: 0.9,
            overflowWrap: "normal",
            wordBreak: "keep-all",
            hyphens: "none",
            overflow: "hidden",
          }}
        >
          {lastName}
        </span>
      </div>
      {showPaid && (
        <span
          className="absolute right-1.5 bottom-1 font-display font-black"
          style={{ fontSize: 13, color: paidColor }}
        >
          {paid != null ? `$${paid}` : "$-"}
        </span>
      )}
    </div>
  );
}

